import { useParams } from "react-router";
import i18n from "../../i18n";
import style from './PaymentStatusScreen.module.css';
import { useEffect, useMemo, useState } from "react";
import getPaymentFlow from "../../Api/Requests/get-payment-flow";
import PaymentDetails from "./Widgets/PaymentDetails";
import PaymentSteps from "./Widgets/PaymentSteps";
import Button from "./Widgets/Button";
import Tips from "./Widgets/Tips";
import Boost from "./Widgets/Boost";
import GetStarted from "./Widgets/GetStarted";
import AddonsList from "./Widgets/AddonsList";
import { IoChevronBack, IoChevronForward, IoCloseSharp } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import { getLang } from "../../Helpers/functions";
import { useSearchParams } from "react-router-dom";
import { updateScreenHistory } from "../../logging/helpers/updateScreenHistory";
import { logOrderEndFail, logOrderEndSuccess } from "../../logging/helpers/commonLoggingFunctions";
import { createOrderEndReporter } from "../../Helpers/order-end";

const MAX_POLL_ATTEMPTS = 30;
const POLL_INTERVAL_MS = 4000;

const PaymentStatusScreen = () => {
    let {paymentSecret,cartId,modelId} = useParams();
    const [paymentFlow,setPaymentFlow] = useState(null);
    const [backTo,setBackTo] = useState(null);
    const [backendCartId,setBackendCartId] = useState(cartId ?? null)
    const [headerColor , setHeaderColor] = useState(null);
    const [pageLoading,setPageLoading] = useState(true);
    const {t} = useTranslation()
    const [searchParams] = useSearchParams();
    const referrerUrl = searchParams.get("referrerUrl"); 
    const [showCloseButton, setShowCloseButton] = useState(false);
    const [paymentSuccess , setPaymentSuccess] = useState(false);

    // the page polls, so the order-end event is reported once per outcome and never while pending
    const reportOrderEnd = useMemo(() => createOrderEndReporter((outcome, id) => {
        if (outcome === 'success') {
            updateScreenHistory('order_success');
            logOrderEndSuccess({ id });
        } else {
            updateScreenHistory('order_failure');
            logOrderEndFail({ id });
        }
    }), []);

    // Resolves true when polling should stop (a final success was received).
    // Results that arrive after `isCancelled()` turns true are ignored.
    const fetchPaymentFlowData = ({ paymentSecret, cartId, modelId, referrerUrl }, isCancelled: () => boolean): Promise<boolean> => {
        return getPaymentFlow({
            paymentSecret,
            cartId,
            modelId,
            referrerUrl
        })
        .then((response) => {
            if (isCancelled()) return true;

            setPaymentFlow(response.data);
            setHeaderColor(getHeaderColor(response.data.background));
            setBackTo(response.data.backTo);
            setShowCloseButton(response.data.background !== 'red');

            if (response.data.success) {
                setPaymentSuccess(true);
            }
            reportOrderEnd(response.data, cartId);

            if (!cartId) {
                setBackendCartId(response.data.cartId);
            }

            setPageLoading(false);
            return !!response.data.success;
        })
        .catch(() => {
            if (isCancelled()) return true;
            setPageLoading(false);
            return false;
        });
    };

    // request-then-wait polling: one request in flight at a time, the attempt cap counts
    // requests sent, and nothing is applied after unmount
    useEffect(() => {
        let cancelled = false;
        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        let attempts = 0;
        const isCancelled = () => cancelled;

        const poll = async () => {
            if (cancelled || attempts >= MAX_POLL_ATTEMPTS) return;
            attempts++;
            const done = await fetchPaymentFlowData({ paymentSecret, cartId, modelId, referrerUrl }, isCancelled);
            if (done || cancelled) return;
            timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
        };
        poll();

        return () => {
            cancelled = true;
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, []);

    const getHeaderColor = (color : string) => {
        let backgroundColor : string =  "#ffffff";

        switch (color) {
            case 'red':
                backgroundColor = '#FAEFF080'
                break;
            case 'green':
                backgroundColor =  '#EDF6F5'
                break;
            case 'yellow':
                return '#FFF5CD80'
                break;
            default:
                break;
        }

        return backgroundColor;
    }
    
    const renderWidgetItem = (widget: any, index: number) => {
        switch (widget.type) {
            case "paymentDetails":
                return (<PaymentDetails
                    key={index}
                    widget={widget}
                />);
            case "paymentSteps":
                return (<PaymentSteps
                    key={index}
                    widget={widget}
                />);
            case "boost":
                return (<Boost
                    key={index}
                    widget={widget}
                    paymentSecret={paymentSecret}
                    cartId={backendCartId}
                    modelId={modelId}
                />);
            case "addons":
                return (<AddonsList
                    key={index}
                    widget={widget}
                />);
            case "getStarted":
                return (<GetStarted
                    key={index}
                    widget={widget}
                    paymentSecret={paymentSecret}
                    cartId={backendCartId}
                    modelId={modelId}
                    referrerUrl={referrerUrl}
                />);
            case "tip":
                return (<Tips
                    key={index}
                    widget={widget}
                />);
            case "button":
                return (<Button
                    key={index}
                    widget={widget}
                    paymentSecret={paymentSecret}
                    cartId={backendCartId}
                    modelId={modelId}
                    referrerUrl={referrerUrl}
                />);
            default:
            return null;
        }
    };

    const help = () => {
        const title = i18n.language == 'ar' ? 'مساعدة' : 'support';
        return `https://jo.opensooq.com/${i18n.language}/${title}`
    }

    const terms = () => {
        return `https://jo.opensooq.com/${i18n.language}/termOfUse`
    }

    const callUs = () => {
        const title = i18n.language == 'ar' ? '/مساعدة/اتصل-بنا' : '/support/contact-us';
        return `https://jo.opensooq.com/${i18n.language}${title}`
    }

    const redirectBackTo = () => {
        window.location.href = backTo;
    }

    const getIcon = (key: string) => {
        return require(`../../Assets/Images/${key}.svg`)
    }

    const getActionIcon = () => {
        switch (showCloseButton) {
            case true:
                return <IoCloseSharp onClick={redirectBackTo}></IoCloseSharp>;
            case false:
                return i18n.language == 'ar' ? <IoChevronForward onClick={redirectBackTo}></IoChevronForward> : <IoChevronBack onClick={redirectBackTo}></IoChevronBack>;
        }
    }

    return <>
        {pageLoading && <div className="d-flex justify-content-center align-items-center vh-100">
            <div className="spinner-border text-primary" role="status">
            </div>
        </div>}
        {!pageLoading && paymentFlow && <div dir={i18n.language=='en'?'ltr':'rtl'} className={style.container}>
            <div className={style.header_payment_screen}>
                <div dir={i18n.language=='en'?'ltr':'rtl'} className={`${style.content_container_wrapper}`}>
                    <header className="d-flex justify-content-between">
                        {getActionIcon()}
                        <a  href={help()} className="text-primary text-decoration-none hoverPointer" style={{fontSize : "14px"}} onClick={help}>{t('navbar.help')}</a>
                    </header>
                </div>
            </div>
            {paymentFlow && <div style={{background : headerColor}}>
                <div className={`${style.content_container_wrapper} justify-content-center`}>
                    <div className={`d-flex align-items-center justify-content-center gap-4 ${style.heigh_105}`}>
                        <img width={44} height={44}  src={paymentFlow.widgets[0].image.replace("{platform}","ios").replace("{size}","1x")}/>
                        <div className={`d-flex flex-column gap-1 ${getLang() == 'ar' ? 'text-end' : 'text-start'}`}>
                            <span className={`${style.text_18} fw-bold`}>{paymentFlow.widgets[0].header}</span>
                            <span className={style.text_14}>{paymentFlow.widgets[0].description}</span>
                        </div>
                    </div>
                </div>
            </div>}
            {paymentFlow && <div className={`${style.content_container} ${style.content_container_wrapper} ${style.container_width} mt-2`}>
                {paymentFlow.widgets.map((widget : any,index : any) => {
                    return renderWidgetItem(widget,index)
                })}
            </div>}
            <div className={`text-center ${style.content_container_wrapper_help} mt-2`} style={{bottom: "0"}}>
                <div className="d-flex justify-content-center align-content-center gap-1">
                    <img height={24} src={getIcon('help')} className="ms-1"/> 
                    <span style={{fontSize : "14px",color: "#000000",paddingTop : "3px"}}>
                        {t('doYouNeedHelp')} <a href={callUs()} className="hoverPointer text-decoration-none">{t('contactUs')}</a>
                    </span>
                </div>
                <a href={terms()} className="hoverPointer mt-1 text-decoration-none" style={{fontSize: "14px",color: "#85858B"}}>
                    {t('terms&Conditions')}
                </a>
            </div>
        </div>}
    </>
}

export default PaymentStatusScreen